import React from "react";
import { useQuery } from "@tanstack/react-query";
import { usePortalStore } from "../../store/portal/PortalStoreContext";
import { usePatientsQuery } from "../usePatientsQuery";
import { usePaymentMethodsQuery } from "../usePaymentMethodsQuery";
import { useWorkspaceQuery } from "../useWorkspaceQuery";
import { portalKeys, useMyAppointmentsQuery, usePortalIdentityKey, } from "./usePortalQueries";
import { useSessionExpiryWatch } from "./useSessionExpiryWatch";
import { PortalPackService } from "../../services/portal/PortalPackService";
import { partitionByTab } from "../../services/portal/types";
const PACKS_STALE_MS = 60 * 1000;
export function usePortalDashboardData() {
    const scope = usePortalStore((s) => s.patientScope);
    const setScope = usePortalStore((s) => s.setPatientScope);
    const channel = usePortalStore((s) => s.channel);
    const countryCode = usePortalStore((s) => s.countryCode);
    const phoneNumber = usePortalStore((s) => s.phoneNumber);
    const email = usePortalStore((s) => s.email);
    const verified = usePortalStore((s) => s.view !== "phone" && s.view !== "code");
    const lookup = React.useMemo(() => channel === "email"
        ? { channel: "email", email }
        : { channel: "phone", countryCode, phoneNumber }, [channel, countryCode, phoneNumber, email]);
    const patientsQuery = usePatientsQuery({
        lookup,
        enabled: verified,
        bookingRevision: 0,
    });
    const patients = React.useMemo(() => patientsQuery.data?.data?.patients ?? [], [patientsQuery.data]);
    React.useEffect(() => {
        if (!patientsQuery.isSuccess)
            return;
        if (patients.length === 0) {
            if (scope === null)
                setScope("ALL");
            return;
        }
        const stillExists = scope === "ALL" || patients.some((patient) => patient.id === scope);
        if (scope !== null && stillExists)
            return;
        setScope(patients.length === 1 ? patients[0].id : "ALL");
    }, [patientsQuery.isSuccess, patients, scope, setScope]);
    const workspaceQuery = useWorkspaceQuery();
    const workspace = workspaceQuery.data;
    const addressId = workspace?.addresses?.[0]?.id ?? null;
    const currency = workspace?.currency ?? null;
    const identity = usePortalIdentityKey();
    const packsQuery = useQuery({
        queryKey: portalKeys.packs(identity, addressId),
        queryFn: () => PortalPackService.listPacks(addressId),
        enabled: verified && addressId != null,
        staleTime: PACKS_STALE_MS,
        retry: 1,
        refetchOnWindowFocus: false,
    });
    const appointmentsQuery = useMyAppointmentsQuery(scope);
    const appointments = appointmentsQuery.data ?? [];
    const paymentMethodsQuery = usePaymentMethodsQuery(verified);
    useSessionExpiryWatch([appointmentsQuery.error, patientsQuery.error]);
    return {
        patients,
        patientsQuery,
        walletPacks: patientsQuery.data?.data?.activeSessionPacks,
        packs: packsQuery.data ?? [],
        packsLoading: packsQuery.isPending && addressId != null,
        appointments,
        appointmentsQuery,
        methods: paymentMethodsQuery.data ?? [],
        addressId,
        currency,
    };
}
export function useAppointmentTabs(appointments) {
    return React.useMemo(() => partitionByTab(appointments), [appointments]);
}
